import { gql } from "@apollo/client";

import { useQuery } from "@apollo/client/react";

const INSTALLED_EXTENSIONS_QUERY = gql`
  query installedExtensions {
    myMarketplaceInstallations(page: { first: 50 }) {
      nodes {
        id
        marketplaceAppId
        grantedPermissions
        marketplaceApp {
          id
          name
          icon
          manifest {
            extensionCdnPath
            extensions {
              type
              label
              activePages
              size {
                maxWidth
                maxHeight
              }
            }
            metafieldNamespaces
          }
        }
      }
    }
  }
`;

interface ExtensionPointConfig {
  type: string;
  label: string;
  activePages: string[];
  size?: {
    maxWidth?: number;
    maxHeight?: number;
  };
}

interface InstalledExtension {
  installationId: string;
  appId: string;
  appName: string;
  appIcon?: string;
  extensionCdnPath?: string;
  grantedPermissions: string[];
  manifestNamespaces: string[];
  extension: ExtensionPointConfig;
}

/**
 * Match a concrete pathname against a URL pattern with `:param` segments.
 * Example: matchPattern("/tournaments/abc123", "/tournaments/:tournamentId") → true
 */
function matchPattern(pathname: string, pattern: string): boolean {
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");

  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every(
    (part, i) => part.startsWith(":") || part === pathParts[i],
  );
}

/**
 * Extract route params from a pathname given a matching pattern.
 * Example: extractParams("/tournaments/abc123", "/tournaments/:tournamentId")
 *   → { tournamentId: "abc123" }
 */
export function extractParams(
  pathname: string,
  pattern: string,
): Record<string, string> {
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");
  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(":")) {
      params[patternParts[i].slice(1)] = pathParts[i];
    }
  }

  return params;
}

export function useInstalledExtensions(pathname: string): {
  extensions: InstalledExtension[];
  loading: boolean;
} {
  const { data, loading } = useQuery<any>(INSTALLED_EXTENSIONS_QUERY);

  const extensions: InstalledExtension[] = [];

  if (data?.myMarketplaceInstallations?.nodes) {
    for (const installation of data.myMarketplaceInstallations.nodes) {
      const manifest = installation.marketplaceApp?.manifest;
      if (!manifest?.extensions) continue;

      for (const ext of manifest.extensions) {
        const matchesPage = ext.activePages.some((pattern: string) =>
          matchPattern(pathname, pattern),
        );

        if (matchesPage) {
          extensions.push({
            installationId: installation.id,
            appId: installation.marketplaceAppId,
            appName: installation.marketplaceApp?.name ?? "Unknown",
            appIcon: installation.marketplaceApp?.icon,
            extensionCdnPath: manifest.extensionCdnPath,
            grantedPermissions: installation.grantedPermissions ?? [],
            manifestNamespaces: manifest.metafieldNamespaces ?? [],
            extension: ext,
          });
        }
      }
    }
  }

  return { extensions, loading };
}
